import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { customersApi } from '../api/customers.api.js';

/**
 * Opens the customer's own interface, as them, read-only.
 *
 * Answering "what does the customer actually see?" from the database is
 * guesswork — a missing IBAN, a reference that never rendered, a balance that
 * reads differently on their dashboard than in the ledger. The interface is
 * the only honest answer.
 *
 * It is also somebody standing inside another person's bank account, so the
 * dialog says plainly what will happen before it happens: the session reads and
 * cannot act, it is recorded against the admin who opened it, and it expires in
 * fifteen minutes. A reason can be given and is recorded, but is not demanded:
 * the record is already complete without it — who looked, whose account, and
 * every page opened — so requiring one taxed every routine look to capture a
 * sentence nobody would read.
 */

/**
 * Where the customer app lives.
 *
 * The console is served from admin.<domain> and the customer app from
 * app.<domain>, so it is derived rather than configured — one fewer setting to
 * be wrong, and it follows the environment automatically. `VITE_APP_URL`
 * overrides it for a local setup where the two are ports, not subdomains.
 */
function customerAppOrigin(): string {
  const configured = import.meta.env['VITE_APP_URL'] as string | undefined;
  if (configured !== undefined && configured !== '') return configured.replace(/\/+$/, '');
  const { origin } = window.location;
  return origin.includes('admin.') ? origin.replace('admin.', 'app.') : origin;
}

export function ViewAsCustomerButton({ customerId }: { customerId: string }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const [reason, setReason] = useState('');

  const start = useMutation({
    mutationFn: () => customersApi.impersonate(customerId, reason.trim()),
    onSuccess: (session) => {
      // Handed over in the fragment, which browsers do not send to servers and
      // which does not land in access logs the way a query string does.
      const url = `${customerAppOrigin()}/#view-as=${encodeURIComponent(session.accessToken)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
      setOpen(false);
    },
    onError: (err: unknown) =>
      setError(err instanceof Error ? err.message : 'Could not open the customer view.'),
  });

  return (
    <>
      <Button variant="outlined" onClick={() => { setError(''); setReason(''); setOpen(true); }}>
        View as customer
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Open this customer’s interface</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2">
              Opens the customer app in a new tab, signed in as this customer, so you can see
              exactly what they see.
            </Typography>
            <Alert severity="info">
              <strong>Read-only.</strong> Anything that would change something — sending money,
              opening an account, ordering a card — is refused. To act on their behalf, use the
              Actions page under your own name.
            </Alert>
            <Alert severity="warning">
              This is recorded. Every page you open is logged against your admin account, together
              with whose account you were in. The session lasts 15 minutes.
            </Alert>
            <TextField
              label="Reason (optional)"
              multiline
              minRows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              helperText="Kept against your admin account. Leave it blank if you are just looking."
            />
            {error !== '' ? <Alert severity="error">{error}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => start.mutate()}
            disabled={start.isPending}
          >
            {start.isPending ? 'Opening…' : 'Open their interface'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
